/**
 * Fire a GA4 event through the global gtag (loaded once in index.html, gated by
 * Consent Mode v2). The event is recorded only when the visitor has granted
 * analytics consent; before that GA models/drops it — so no extra consent check
 * is needed here. No-op when gtag is absent (SSR/prerender, ad-blockers, or
 * analytics not yet loaded). Use GA4 recommended event names (e.g. generate_lead).
 */
export function trackEvent(name: string, params?: Record<string, unknown>): void {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  window.gtag('event', name, { ...experimentParams(), ...(params ?? {}) });
}

/**
 * The A/B variant this page was served as, if any.
 *
 * Stamped into the HTML at PRERENDER time (see prerender.mts) rather than
 * injected at request time, so it is already present when the first event
 * fires and needs no rewriting in the Worker.
 *
 * It carries no identifier — it says "this page is variant X", never "you are
 * person Y" — but it is still only attached to events that are themselves
 * consent-gated, because an experiment readout is analytics either way.
 *
 * Explicit params win: a caller passing its own `ix_variant` is describing
 * something more specific than the page-level assignment.
 */
export function experimentParams(): Record<string, string> {
  const v = (window as { __ix_variant?: unknown }).__ix_variant;
  return typeof v === 'string' && v !== 'control' ? { ix_variant: v } : {};
}
